import { IODADataPoint, IODALatencyDataPoint, CountryStatus, RegionScore, GeoAsnScore } from '../types/ioda';
import { getApiBaseUrl } from '../utils/apiBaseUrl';

const API_BASE_URL = getApiBaseUrl();
const INGEST_API_KEY = import.meta.env.VITE_INGEST_API_KEY as string | undefined;

/** Result shape so callers can distinguish HTTP failures from empty data */
export type HistoricalResult<T> = { data: T; error: string | null };

/** GET /api/data/dashboard-core — one round-trip for main charts + status */
export type DashboardCorePayload = {
  meta: {
    from: number;
    until: number;
    wideFrom: number;
    maxPoints: number;
    spanSec: number;
    meritRawCount: number;
    lossRawCount: number;
    latencyRawCount: number;
    gtrWebSearchRawCount?: number;
    generatedAt: number;
    maxMeritTimestamp: number | null;
    lastIngestSuccessUtc?: number | null;
    ingestHasError?: boolean;
  };
  status: CountryStatus;
  meritNt: { data: IODADataPoint[] };
  networkScore: { data: IODADataPoint[] };
  latency: { data: IODALatencyDataPoint[] };
  bgp: { data: IODADataPoint[] };
  gtrNorm: { data: IODADataPoint[] };
  /** Web-search slice of normalized traffic (~30m buckets upstream) */
  gtrNormWebSearch?: { data: IODADataPoint[] };
};

function jsonIngestHeaders(): HeadersInit {
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  if (INGEST_API_KEY && String(INGEST_API_KEY).trim() !== '') {
    h['X-Ingest-Key'] = String(INGEST_API_KEY).trim();
  }
  return h;
}

export type IngestPostResult =
  | { ok: true; stored: number; skipped: number }
  | { ok: false; error: string };

export class HistoricalService {
  /**
   * Bundled main series + status (downsampled on server for long ranges). Prefer over 6 parallel calls.
   */
  static async getDashboardCore(
    from: number,
    until: number,
    maxPoints?: number
  ): Promise<HistoricalResult<DashboardCorePayload | null>> {
    try {
      let url = `${API_BASE_URL}/data/dashboard-core?from=${from}&until=${until}`;
      if (maxPoints != null && Number.isFinite(maxPoints)) {
        url += `&maxPoints=${Math.floor(maxPoints)}`;
      }
      const response = await fetch(url);
      if (!response.ok) {
        return { data: null, error: `dashboard-core HTTP ${response.status}` };
      }
      const data = (await response.json()) as DashboardCorePayload;
      return { data, error: null };
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      console.error('Error fetching dashboard-core:', error);
      return { data: null, error: msg };
    }
  }

  /**
   * Get connectivity data from database for date range
   * @param datasource Optional: merit-nt, ping-slash24, bgp, gtr-norm, gtr-norm-web-search
   */
  static async getConnectivityData(
    from: number,
    until: number,
    datasource?: string
  ): Promise<HistoricalResult<IODADataPoint[]>> {
    try {
      let url = `${API_BASE_URL}/data/connectivity?from=${from}&until=${until}`;
      if (datasource) url += `&datasource=${encodeURIComponent(datasource)}`;
      const response = await fetch(url);

      if (!response.ok) {
        return { data: [], error: `connectivity HTTP ${response.status}` };
      }

      const data = await response.json();
      return { data: data.data || [], error: null };
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      console.error('Error fetching historical connectivity data:', error);
      return { data: [], error: msg };
    }
  }

  /**
   * Get latency data from database for date range
   */
  static async getLatencyData(
    from: number,
    until: number
  ): Promise<HistoricalResult<IODALatencyDataPoint[]>> {
    try {
      const url = `${API_BASE_URL}/data/latency?from=${from}&until=${until}`;
      const response = await fetch(url);

      if (!response.ok) {
        return { data: [], error: `latency HTTP ${response.status}` };
      }

      const data = await response.json();
      const mapped = (data.data || []).map((r: Record<string, number>) => ({
        from: r.from,
        until: r.until,
        meanLatency: r.meanLatency,
        medianLatency: r.medianLatency,
        p90Latency: r.p90Latency,
        minLatency: r.minLatency,
        maxLatency: r.maxLatency,
      }));
      return { data: mapped, error: null };
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      console.error('Error fetching latency data:', error);
      return { data: [], error: msg };
    }
  }

  /**
   * Get computed status from database (no live upstream fetch in this call)
   */
  static async getStatus(): Promise<HistoricalResult<CountryStatus | null>> {
    try {
      const url = `${API_BASE_URL}/data/status`;
      const response = await fetch(url);

      if (!response.ok) {
        return { data: null, error: `status HTTP ${response.status}` };
      }

      const data = await response.json();
      return { data: data as CountryStatus, error: null };
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      console.error('Error fetching status:', error);
      return { data: null, error: msg };
    }
  }

  /**
   * Get regional outage scores from database (ordered by score DESC: highest outage first)
   */
  static async getRegionData(from?: number, until?: number): Promise<HistoricalResult<RegionScore[]>> {
    try {
      let url = `${API_BASE_URL}/data/regions`;
      if (from != null && until != null) {
        url += `?from=${from}&until=${until}`;
      }
      const response = await fetch(url);

      if (!response.ok) {
        return { data: [], error: `regions HTTP ${response.status}` };
      }

      const json = await response.json();
      const arr = json?.data;
      if (!Array.isArray(arr)) return { data: [], error: null };
      return { data: arr, error: null };
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      console.error('Error fetching region data:', error);
      return { data: [], error: msg };
    }
  }

  /**
   * Get affected data centers / ASNs from database (ordered by score DESC)
   */
  static async getGeoAsnData(from?: number, until?: number): Promise<HistoricalResult<GeoAsnScore[]>> {
    try {
      let url = `${API_BASE_URL}/data/geoasn`;
      if (from != null && until != null) {
        url += `?from=${from}&until=${until}`;
      }
      const response = await fetch(url);

      if (!response.ok) {
        return { data: [], error: `geoasn HTTP ${response.status}` };
      }

      const json = await response.json();
      const arr = json?.data;
      if (!Array.isArray(arr)) return { data: [], error: null };
      return { data: arr, error: null };
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      console.error('Error fetching geoasn data:', error);
      return { data: [], error: msg };
    }
  }

  /**
   * Get network score data (ping-slash24-loss connectivity 0–100) for date range
   */
  static async getNetworkScoreData(
    from: number,
    until: number
  ): Promise<HistoricalResult<IODADataPoint[]>> {
    try {
      const url = `${API_BASE_URL}/data/network-score?from=${from}&until=${until}`;
      const response = await fetch(url);

      if (!response.ok) {
        return { data: [], error: `network-score HTTP ${response.status}` };
      }

      const data = await response.json();
      return { data: data.data || [], error: null };
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      console.error('Error fetching historical network score data:', error);
      return { data: [], error: msg };
    }
  }

  /**
   * Store connectivity data to database (optional VITE_INGEST_API_KEY)
   */
  static async storeConnectivityData(data: IODADataPoint[]): Promise<IngestPostResult> {
    try {
      const url = `${API_BASE_URL}/data/connectivity`;
      const response = await fetch(url, {
        method: 'POST',
        headers: jsonIngestHeaders(),
        body: JSON.stringify({ data }),
      });

      if (!response.ok) {
        const errBody = await response.text();
        return { ok: false, error: `HTTP ${response.status}: ${errBody.slice(0, 200)}` };
      }
      const j = await response.json();
      return { ok: true, stored: j.stored ?? 0, skipped: j.skipped ?? 0 };
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      console.error('Error storing connectivity data:', error);
      return { ok: false, error: msg };
    }
  }

  /**
   * Store network score data to database (optional VITE_INGEST_API_KEY)
   */
  static async storeNetworkScoreData(data: IODADataPoint[]): Promise<IngestPostResult> {
    try {
      const url = `${API_BASE_URL}/data/network-score`;
      const response = await fetch(url, {
        method: 'POST',
        headers: jsonIngestHeaders(),
        body: JSON.stringify({ data }),
      });

      if (!response.ok) {
        const errBody = await response.text();
        return { ok: false, error: `HTTP ${response.status}: ${errBody.slice(0, 200)}` };
      }
      const j = await response.json();
      return { ok: true, stored: j.stored ?? 0, skipped: j.skipped ?? 0 };
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      console.error('Error storing network score data:', error);
      return { ok: false, error: msg };
    }
  }

  static async getOutageAnnotations(limit?: number): Promise<HistoricalResult<OutageAnnotation[]>> {
    try {
      const url = limit ? `${API_BASE_URL}/data/outages?limit=${limit}` : `${API_BASE_URL}/data/outages`;
      const response = await fetch(url);
      if (!response.ok) return { data: [], error: `outages HTTP ${response.status}` };
      const json = await response.json();
      return { data: json.data ?? [], error: null };
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      console.error('Error fetching outage annotations:', error);
      return { data: [], error: msg };
    }
  }

  static async getTrafficTimeseries(
    from: number,
    until: number
  ): Promise<HistoricalResult<TrafficTimeseriesPoint[]>> {
    try {
      const url = `${API_BASE_URL}/data/traffic?from=${from}&until=${until}`;
      const response = await fetch(url);
      if (!response.ok) return { data: [], error: `traffic HTTP ${response.status}` };
      const json = await response.json();
      return { data: json.data ?? [], error: null };
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      console.error('Error fetching traffic timeseries:', error);
      return { data: [], error: msg };
    }
  }

  static async getTrafficByRegion(metric?: string): Promise<HistoricalResult<TrafficByRegion[]>> {
    try {
      const url = metric
        ? `${API_BASE_URL}/data/traffic-by-region?metric=${encodeURIComponent(metric)}`
        : `${API_BASE_URL}/data/traffic-by-region`;
      const response = await fetch(url);
      if (!response.ok) return { data: [], error: `traffic-by-region HTTP ${response.status}` };
      const json = await response.json();
      return { data: json.data ?? [], error: null };
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      console.error('Error fetching traffic by region:', error);
      return { data: [], error: msg };
    }
  }
}

export interface OutageAnnotation {
  startDate: string;
  endDate: string | null;
  cause: string | null;
  outageType: string | null;
  scope: string | null;
  locations: string[] | null;
  asns: unknown;
  linkedUrl: string | null;
  fetchedAt: number;
}

export interface TrafficTimeseriesPoint {
  from: number;
  until: number;
  value: number;
  seriesName: string;
  metric: string;
}

export interface TrafficByRegion {
  regionCode: string;
  regionName: string | null;
  value: number;
  metric: string;
  fetchedAt: number;
}
